/**
 * The environment ladder, shared with every other Nebulize repo through the sdk.
 * Pure and browser-free on purpose: vite.config.ts imports it too.
 */

import type { Environment } from "@nebulize/sdk";

/** Keyed by Environment so adding a rung in the sdk fails this file at compile time. */
const ENDPOINT_DEFAULTS = {
  local: { apiPath: "/api", wsPath: "/ws", usesDevProxy: true },
  dev: { apiPath: "/api", wsPath: "/ws", usesDevProxy: false },
  prod: { apiPath: "/api", wsPath: "/ws", usesDevProxy: false },
} as const satisfies Record<Environment, EndpointDefaults>;

export interface EndpointDefaults {
  /** Path appended to the resolved origin when VITE_API_BASE_URL is unset. */
  readonly apiPath: string;
  /** Path appended to the resolved origin when VITE_WS_BASE_URL is unset. */
  readonly wsPath: string;
  /** True only where the Vite dev server proxies /api and /ws to the local api. */
  readonly usesDevProxy: boolean;
}

export const ENVIRONMENTS = Object.keys(ENDPOINT_DEFAULTS) as readonly Environment[];

/** Vite's own mode names, which are not environment names. */
const MODE_ALIASES: Record<string, Environment> = {
  development: "local",
  test: "local",
  production: "prod",
};

export function isEnvironment(value: string): value is Environment {
  return Object.prototype.hasOwnProperty.call(ENDPOINT_DEFAULTS, value);
}

export function endpointDefaults(environment: Environment): EndpointDefaults {
  return ENDPOINT_DEFAULTS[environment];
}

/**
 * Resolve the environment from VITE_NEBULIZE_ENV, falling back to the Vite mode.
 * Throws rather than defaulting: a typo must not quietly build as prod.
 */
export function resolveEnvironment(raw: string | undefined, mode: string): Environment {
  const candidate = raw ?? MODE_ALIASES[mode] ?? mode;
  if (!isEnvironment(candidate)) {
    throw new Error(
      `VITE_NEBULIZE_ENV must be one of ${ENVIRONMENTS.join(", ")}, received ${JSON.stringify(candidate)}`,
    );
  }
  return candidate;
}
